import type { Subnet } from 'openstack/components/networks/model'
import {
  addAttachmentRow,
  NetworkAttachmentRow,
  NetworkAttachments,
  removeAttachmentRow,
  updateAttachmentRow,
} from '../CreateVirtualMachineModal.helpers'
import {
  applyFirstRowChange,
  applyParentPortSelection,
  applyParentSubnetChange,
  applySubRowPortSelection,
  applySubRowSubnetChange,
  getFirstAttachmentRow,
} from './networkAttachmentRows'

/**
 * PCD-5420: the set of "save" callbacks the Networking table uses.
 *
 * Think of this as a small remote control for the table. Each callback has a clear name like
 * `setParentSubnet`, so a table cell can just call `handlers.setParentSubnet(networkId, subnet)`.
 * Under the hood every callback does two things: it runs one of the pure rule functions in
 * `networkAttachmentRows.ts` to figure out the new list of NIC rows, then it saves that result
 * back into the form state. Without these handlers, every cell would have to repeat that same
 * `getParamsUpdater('networkAttachments')(applyX(attachments, ...))` wiring by hand.
 *
 * Note this is a plain function, not a React hook. The callbacks hold no state of their own, and
 * the code that uses them (`getManagedNetworkColumns`) builds table columns rather than rendering
 * a component, so React's "rules of hooks" don't apply here.
 */
export interface NetworkAttachmentHandlers {
  setParentSubnet: (networkId: string, subnet: Subnet | '') => void
  setParentPorts: (networkId: string, newValues: string[], prevValues: string[]) => void
  // Set a single port on the network's one-and-only row (used by simple/L2 networks, which have
  // exactly one NIC). Writes to that existing row rather than adding a new one, so re-selecting a
  // port replaces it instead of creating a duplicate NIC.
  setParentPort: (networkId: string, port: string) => void
  setParentFixedIp: (networkId: string, fixedIP: string) => void
  setSubRowSubnet: (networkId: string, anchorRowId: string, subnet: Subnet | '') => void
  setSubRowPorts: (
    networkId: string,
    anchor: NetworkAttachmentRow,
    newValues: string[],
    prevValues: string[],
  ) => void
  setRowFixedIp: (networkId: string, rowId: string, fixedIP: string) => void
  addRow: (networkId: string, network: any) => void
  removeRow: (networkId: string, rowId: string) => void
}

export const createNetworkAttachmentHandlers = (
  attachments: NetworkAttachments,
  getParamsUpdater: (key: string) => (value: any) => void,
): NetworkAttachmentHandlers => {
  const commit = getParamsUpdater('networkAttachments')
  return {
    setParentSubnet: (networkId, subnet) =>
      commit(applyParentSubnetChange(attachments, networkId, subnet)),
    setParentPorts: (networkId, newValues, prevValues) =>
      commit(applyParentPortSelection(attachments, networkId, newValues, prevValues)),
    setParentPort: (networkId, port) =>
      commit(
        updateAttachmentRow(
          attachments,
          networkId,
          getFirstAttachmentRow(attachments, networkId).rowId,
          { port },
        ),
      ),
    setParentFixedIp: (networkId, fixedIP) =>
      commit(applyFirstRowChange(attachments, networkId, { fixedIP })),
    setSubRowSubnet: (networkId, anchorRowId, subnet) =>
      commit(applySubRowSubnetChange(attachments, networkId, anchorRowId, subnet)),
    setSubRowPorts: (networkId, anchor, newValues, prevValues) =>
      commit(applySubRowPortSelection(attachments, networkId, anchor, newValues, prevValues)),
    setRowFixedIp: (networkId, rowId, fixedIP) =>
      commit(updateAttachmentRow(attachments, networkId, rowId, { fixedIP })),
    addRow: (networkId, network) => commit(addAttachmentRow(attachments, networkId, network)),
    removeRow: (networkId, rowId) => commit(removeAttachmentRow(attachments, networkId, rowId)),
  }
}
